import { useState } from 'react';
import { aiClient, AiBudgetExhaustedError } from './lib/aiClient.js';

// Opus 4.7 + web_search verifier. Editor pastes (or selects) a single claim,
// clicks Verify, gets a structured verdict + cited URLs.
const STATUS_STYLE = {
  green: 'bg-hxd-ok text-white border-hxd-ok',
  yellow: 'bg-hxd-warn text-white border-hxd-warn',
  red: 'bg-hxd-err text-white border-hxd-err',
};

export default function VerifyTool({
  articleId,
  isoDate,
  original,
  onResult,
  onClose,
}) {
  const [claim, setClaim] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runVerify = async () => {
    if (!claim.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await aiClient.verify({
        articleId,
        isoDate,
        claim: claim.trim(),
        original,
      });
      setResult(data);
      onResult?.(data);
    } catch (err) {
      if (err instanceof AiBudgetExhaustedError) {
        setError('AI budget hit — Verify disabled until the cap resets.');
      } else {
        setError(err.message || 'Verify failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-hxd-surface border border-hxd-border rounded-[3px] p-3 mt-3">
      <div className="flex items-center justify-between mb-3">
        <span className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted">
          Verify · Opus 4.7 + web search
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-hxd-muted hover:text-hxd-ink"
        >
          Close
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mb-3">
        <textarea
          rows={2}
          className="hxd-input bg-white"
          value={claim}
          onChange={(e) => setClaim(e.target.value)}
          placeholder="Paste a single claim to verify — e.g. 'Joby flew an uninterrupted 200-mile flight.'"
          disabled={loading}
        />
        <button
          type="button"
          onClick={runVerify}
          disabled={loading || !claim.trim()}
          className="hxd-btn hxd-btn-secondary hxd-btn-sm self-start"
        >
          {loading ? 'Verifying…' : 'Verify'}
        </button>
      </div>

      {error && (
        <p className="text-[12px] text-hxd-err mb-3 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </p>
      )}

      {result && (
        <div className="bg-white border border-hxd-border-soft rounded p-3 space-y-3">
          <div className="flex items-center gap-2">
            <span
              className={`${STATUS_STYLE[result.status]} font-display text-[10px] font-bold uppercase tracking-display-sm px-2 py-0.5 rounded-[2px] border`}
            >
              {result.status}
            </span>
            <span className="text-[11px] text-hxd-muted">
              confidence {Math.round((result.confidence || 0) * 100)}%
            </span>
          </div>
          {result.note && <p className="text-[13px] text-hxd-ink">{result.note}</p>}
          {Array.isArray(result.sources) && result.sources.length > 0 && (
            <div>
              <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted mb-1">
                Sources ({result.sources.length})
              </div>
              <ul className="space-y-1">
                {result.sources.map((s, i) => (
                  <li key={i} className="text-[12px] truncate">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-hxd-orange hover:underline"
                    >
                      {s.title || s.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
